import React from "react";

const rarityColors = {
  Basic: "#fff",
  Fine: "#4fc3f7",
  Masterwork: "#43a047",
  Rare: "#fbc02d",
  Exotic: "#ff9800",
  Ascended: "#e040fb",
  Legendary: "#8e24aa",
};

export default function ItemsGrid({
  items = [],
  selectedItemId = null,
  hoveredItemId = null,
  renderMode = "icons",
  detailedCardMaxWidth = 228,
  assignmentMode = false,
  showControls = false,
  onItemClick,
  onItemMouseEnter,
  onItemMouseLeave,
  onDecrement,
  onIncrement,
  onRemove,
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, minHeight: 120, overflow: "visible", position: "relative" }}>
      {items.map((item, idx) => {
        const itemId = item?.id;
        const isHovered = hoveredItemId != null && String(hoveredItemId) === String(itemId);
        const isSelected = selectedItemId != null && String(selectedItemId) === String(itemId);
        const baseBorderColor = rarityColors[item?.rarity] || "#bbb";
        const borderWidth = isSelected ? 4 : 3;
        const borderColor = isSelected ? "#90e0ef" : baseBorderColor;
        const boxShadow = isSelected
          ? "0 0 22px rgba(144, 224, 239, 0.95), 0 0 10px rgba(144, 224, 239, 0.9)"
          : isHovered
            ? "0 0 8px #888"
            : undefined;

        const iconTile = (
          <div
            style={{
              position: "relative",
              width: 56,
              height: 56,
              border: `${borderWidth}px solid ${borderColor}`,
              borderRadius: 8,
              overflow: "visible",
              background: isSelected ? "#111827" : "#222",
              boxShadow,
              transition: "box-shadow 0.2s, border-color 0.2s, background-color 0.2s",
              zIndex: isHovered || isSelected ? 20 : 1,
              flexShrink: 0,
            }}
          >
            <img
              src={item?.icon}
              alt={item?.name || "item"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                opacity: (item?.count ?? 1) === 0 ? 0.3 : 1,
                pointerEvents: "auto",
              }}
            />

            {isHovered && showControls && !assignmentMode && (
              <>
                <button
                  title="Decrement"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDecrement?.(itemId);
                  }}
                  style={{ position: "absolute", top: 3, left: 3, width: 18, height: 18, background: "#fbc02d", color: "#222", border: "none", borderRadius: 4, fontWeight: "bold", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 201, padding: 0 }}
                >
                  -
                </button>
                <button
                  title="Increment"
                  onClick={(event) => {
                    event.stopPropagation();
                    onIncrement?.(itemId);
                  }}
                  style={{ position: "absolute", top: 3, right: 3, width: 18, height: 18, background: "#43a047", color: "#fff", border: "none", borderRadius: 4, fontWeight: "bold", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 201, padding: 0 }}
                >
                  +
                </button>
                <button
                  title="Remove"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove?.(itemId);
                  }}
                  style={{ position: "absolute", bottom: 3, left: 3, width: 18, height: 18, background: "#e74c3c", color: "#fff", border: "none", borderRadius: 4, fontWeight: "bold", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 201, padding: 0 }}
                >
                  x
                </button>
              </>
            )}

            {item?.count != null && (
              <span style={{ position: "absolute", right: 4, bottom: 2, background: "rgba(0,0,0,0.7)", color: "#fff", fontWeight: "bold", fontSize: 16, padding: "0 6px", borderRadius: 6, pointerEvents: "none" }}>
                {item.count}
              </span>
            )}
          </div>
        );

        if (renderMode === "detailed") {
          return (
            <button
              key={`${itemId}-${idx}`}
              type="button"
              className="names-btn"
              onMouseEnter={(event) => onItemMouseEnter?.(item, event)}
              onMouseLeave={() => onItemMouseLeave?.(item)}
              onClick={() => onItemClick?.(item)}
              title={item?.name || "Unknown item"}
              style={{
                display: "grid",
                gridTemplateColumns: "56px minmax(0, 1fr)",
                alignItems: "center",
                gap: 8,
                width: `min(100%, ${detailedCardMaxWidth}px)`,
                padding: 7,
                borderRadius: 12,
                border: "1px solid rgba(80,180,255,0.24)",
                background: isSelected
                  ? "linear-gradient(180deg, rgba(17, 62, 96, 0.72), rgba(5, 16, 34, 0.94))"
                  : "linear-gradient(180deg, rgba(8, 24, 50, 0.92), rgba(5, 16, 34, 0.88))",
                cursor: assignmentMode ? ((item?.count ?? 0) > 0 ? "copy" : "not-allowed") : "pointer",
                textAlign: "left",
              }}
            >
              {iconTile}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, color: rarityColors[item?.rarity] || "#90e0ef", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item?.name || "Unknown item"}
                </div>
                <div style={{ color: "rgba(214,232,255,0.76)", fontSize: 11, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item?.type || "Unknown type"}
                  {item?.rarity ? ` · ${item.rarity}` : ""}
                </div>
              </div>
            </button>
          );
        }

        return (
          <div
            key={`${itemId}-${idx}`}
            onMouseEnter={(event) => onItemMouseEnter?.(item, event)}
            onMouseLeave={() => onItemMouseLeave?.(item)}
            onClick={() => onItemClick?.(item)}
            title={item?.name || "Unknown item"}
            style={{ cursor: assignmentMode ? ((item?.count ?? 0) > 0 ? "copy" : "not-allowed") : "pointer" }}
          >
            {iconTile}
          </div>
        );
      })}
    </div>
  );
}
